import { FormattedMessage, useIntl } from "react-intl";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import Loading from "../../components/common/Loading/Loading";
import Dropdown from "./Dropdown";

import {
	TGetTikTokVerificationAttributePayload,
	TProduct,
	TTikTokVerificationSelectedValues,
} from "../../../api/api.types";
import {
	useGetTikTokAttributesQuery,
	useSaveTikTokAttributesMutation,
} from "../../../api/productsAPI";

import "./TikTokProductVerification.css";

type TikTokProductVerificationProps = {
	show: boolean;
	onHide: () => void;
	products: number[];
	productsData: TProduct[];
};

const TikTokProductVerification: FC<TikTokProductVerificationProps> = ({
	show,
	onHide,
	products,
	productsData,
}) => {
	const [selectedValues, setSelectedValues] =
		useState<TTikTokVerificationSelectedValues>({});
	const [currentProduct, setCurrentProduct] = useState<number>(products[0]);
	const [completedProducts, setCompletedProducts] = useState<number[]>([]);
	const [sizeChart, setSizeChart] = useState<File | null>(null);
	const [errorFields, setErrorFields] = useState<string[]>([]);

	const sizeChartInputRef = useRef<HTMLInputElement>(null);

	const currentProductData = useMemo(
		() => productsData.find((item) => item.id === currentProduct),
		[currentProduct, productsData]
	);
	const isLast = useMemo(
		() => completedProducts.length === products.length - 1,
		[completedProducts, products]
	);

	const intl = useIntl();

	const { data, isLoading, isFetching, isError } =
		useGetTikTokAttributesQuery(currentProduct, {
			skip: !currentProduct,
			refetchOnMountOrArgChange: true,
		});
	const [saveAttributes, { isLoading: isSaving }] =
		useSaveTikTokAttributesMutation();

	const handleUploadSizeChart = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];

		if (file) {
			setSizeChart(file);
		} else {
			setSizeChart(null);
		}

		e.target.value = "";
	};

	const handleNext = () => {
		if (!data) {
			setCompletedProducts((prev) => [...prev, currentProduct]);
			setErrorFields([]);
			return;
		}

		let isValid = true;

		for (const attribute of data.attributes) {
			if (!selectedValues[attribute.id]) {
				setErrorFields((prev) => [...prev, attribute.id]);
				isValid = false;
			}
		}

		if (isValid) {
			if (!sizeChart && data.size_chart_required) {
				toast.error(
					intl.formatMessage({
						id: "pleaseUploadASizeChartForTheProduct",
					})
				);
				return;
			}

			const formData = new FormData();

			const attributesPayload: TGetTikTokVerificationAttributePayload[] =
				data.attributes.map((attribute) => ({
					id: attribute.id,
					attribute_name: attribute.name,
					attribute_value: selectedValues[attribute.id].name,
					attribute_value_id:
						selectedValues[attribute.id].id === "custom"
							? null
							: selectedValues[attribute.id].id,
				}));

			formData.append(
				"tiktok_product_attributes",
				JSON.stringify(attributesPayload)
			);

			if (sizeChart && data.size_chart_required) {
				formData.append("file", sizeChart);
			}

			saveAttributes({
				id: currentProduct,
				data: formData,
			}).then((res) => {
				if ("error" in res) {
					const error = (res.error as { data: { error: string[] } })
						?.data?.error;

					if (error && error?.length > 0) {
						toast.error(error[0]);
					} else {
						toast.error(
							intl.formatMessage({
								id: "anErrorOccurredWhileSavingTheAttributes",
							})
						);
					}
				} else {
					setCompletedProducts((prev) => [...prev, currentProduct]);
					setErrorFields([]);

					if (completedProducts.length < products.length - 1) {
						setCurrentProduct(
							products[completedProducts.length + 1]
						);
						setSelectedValues({});
					} else {
						toast.success(
							intl.formatMessage({
								id: "allProductsHaveBeenVerified",
							})
						);
						onHide();
					}
				}
			});
		} else {
			setErrorFields((prev) => [
				...prev,
				...data.attributes.map((item) => item.id),
			]);
		}
	};

	useEffect(() => {
		if (isError) {
			if (completedProducts.length < products.length - 1) {
				setCompletedProducts((prev) => [...prev, currentProduct]);
				toast.error(
					intl.formatMessage(
						{
							id: "unableToFetchRequiredAttributesForProduct",
						},
						{ name: currentProductData?.title || "" }
					)
				);
				setCurrentProduct(products[completedProducts.length + 1]);
				setSelectedValues({});
			} else {
				toast.error(
					intl.formatMessage(
						{
							id: "unableToFetchRequiredAttributesForProduct",
						},
						{ name: currentProductData?.title || "" }
					)
				);
				onHide();
			}
		}
	}, [
		isError,
		completedProducts,
		products,
		currentProduct,
		onHide,
		currentProductData,
		intl,
	]);

	return (
		<Modal className="modal-500" centered show={show} onHide={onHide}>
			<Modal.Header className="modal__header" closeButton>
				<Modal.Title className="modal__title">
					<FormattedMessage id="verifyForTikTok" />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="pt-0 d-flex flex-column gap-3">
				<p>
					<FormattedMessage id="youHaveCompletedTheVerificationForThe" />{" "}
					<span style={{ color: "#4db8ff", fontWeight: "500" }}>
						{completedProducts.length}{" "}
						<FormattedMessage id="outOf" /> {products.length}
					</span>{" "}
					<FormattedMessage id="selectedProductsWithDot" />
				</p>
				<div className="tiktok-verification__current-product">
					<span className="tiktok-verification__current-product-label">
						<FormattedMessage id="currentProduct" />
					</span>
					<div className="tiktok-verification__current-product-info">
						{currentProductData?.variants[0]?.images[0]?.image ||
						currentProductData?.variants[0]?.images[0]?.url ? (
							<img
								width={50}
								src={
									currentProductData?.variants[0]?.images[0]
										?.image
										? currentProductData?.variants[0]
												?.images[0]?.image
										: currentProductData?.variants[0]
												?.images[0]?.url
								}
								alt=""
							/>
						) : (
							<div className="empty-image-box"></div>
						)}
						<div className="tiktok-verification__current-product-info-text">
							<p className="tiktok-verification__current-product-info-title">
								{currentProductData?.title}
							</p>
						</div>
					</div>
				</div>
				<Loading
					isLoading={isLoading || isFetching || isSaving}
					scale={0.8}
					loadingText={
						isSaving
							? intl.formatMessage({ id: "savingAttributes" })
							: intl.formatMessage({
									id: "loadingRequiredAttributes",
								})
					}
				>
					<>
						{data && data.size_chart_required && (
							<div className="tiktok-attributes__upload-size-chart">
								<div className="tiktok-attribute-dropdown__label">
									<span>
										<FormattedMessage id="sizeChart" />{" "}
										<span style={{ color: "red" }}>*</span>
									</span>
								</div>
								<button
									onClick={() =>
										sizeChartInputRef.current?.click()
									}
									className="ice-button w-100"
								>
									{sizeChart ? (
										sizeChart.name
									) : (
										<FormattedMessage id="uploadFile" />
									)}
								</button>
								<input
									onChange={handleUploadSizeChart}
									ref={sizeChartInputRef}
									className="d-none"
									accept="image/*"
									type="file"
								/>
							</div>
						)}
						<div className="tiktok-attributes-list">
							{data && data.attributes.length > 0
								? data.attributes.map((item) => (
										<Dropdown
											key={item.id}
											attribute={item}
											setSelectedValues={
												setSelectedValues
											}
											selectedValues={selectedValues}
											errorFields={errorFields}
											setErrorFields={setErrorFields}
										/>
									))
								: null}
						</div>
					</>
				</Loading>
				<div className="tiktok-attributes-list__buttons">
					<button onClick={onHide} className="secondary-button w-100">
						<FormattedMessage id="close" />
					</button>
					<button
						disabled={isLoading || isFetching || isSaving}
						onClick={handleNext}
						className="ice-button w-100"
					>
						{isLast ? (
							<FormattedMessage id="finish" />
						) : (
							<FormattedMessage id="next" />
						)}
					</button>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default TikTokProductVerification;
